import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import Chart, { ChartConfiguration } from 'chart.js/auto';
import { ChartCountV2, ReportV2Service } from '../../services/report-v2.service';
import { CommonModule } from '@angular/common';

interface CreateOption {
  title: string;
  description: string;
  route: string;
  icon: string;
  disabled: boolean;
}

@Component({
  selector: 'app-chart-data-v2',
  templateUrl: './chart-data.html',
  styleUrls: ['./chart-data.scss'],
  imports: [CommonModule, RouterLink, MatFormFieldModule, MatDatepickerModule],
  providers: [provideNativeDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateReportV2Component {
  private reportV2Service = inject(ReportV2Service);
  @Output() dateRangeSelected = new EventEmitter<{ fromDate: Date | null; toDate: Date | null }>();

  reportCount: number = 0;
  legendItems: LegendItem[] = [];
  labels: string[] = [];

  @ViewChild('reportChart', { static: true })
  reportChart!: ElementRef<HTMLCanvasElement>;

  data = {
    labels: this.labels,
    datasets: [
      {
        label: 'Report Status',
        backgroundColor: ['#194951', '#FB923C', '#FED219', '#30BFC2','#FF0000', '#22C55E' ],
        pointStyle: [],
        pointRadius: 0,
        hoverOffset: 0,
        borderWidth: 0,
        spacing: 0,
        cutout: '75%' as any,
      } as any,
    ],
  };

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  centerTextPlugin = {
    id: 'centerText',
    afterDatasetsDraw: (chart: any) => {
      const { ctx, chartArea, width, height } = chart;
      if (!chartArea) return;
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;

      ctx.save();
      const fontSize = Math.min(width, height) / 5;
      ctx.font = `bold ${fontSize}px AECOMSans`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#000';
      const yCorrection = chartArea.height * 0.03;
      ctx.fillText(String(this.reportCount), centerX, centerY + yCorrection);
      ctx.restore();
    },
  };

  config: ChartConfiguration = {
    type: 'doughnut',
    data: this.data,
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 3.3,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
          },
          onClick: (event, legendItem, legend) => {
            const index = legendItem.index!;
            this.toggleLegend(index);
            const chart = legend.chart;
            chart.toggleDataVisibility(index);
            chart.update();
          },
        },
      },
    },
    plugins: [this.centerTextPlugin],
  };

  public chart: any;



  createOptions: CreateOption[] = [
    
    {
      title: 'Create Report',
      description: 'Create report from a template',
      route: '/update-report',
      icon: '/assets/icons/create-report.svg',
      disabled: false,
    }
   
  ];

  ngOnInit() {
    this.reportV2Service.refreshChartData$.subscribe(() => {
      this.getReportCountData();
    });
    this.getReportCountData();
  }

  @ViewChild(MatDateRangeInput) rangeInput!: MatDateRangeInput<Date>;

  onDateChange() {
    const fromDate = this.rangeInput?.value?.start ?? null;
    const toDate = this.rangeInput?.value?.end ?? null;
    this.dateRangeSelected.emit({ fromDate, toDate });
  }

  getReportCountData() {
    this.reportV2Service.getReportCount().subscribe({
      next: (res) => {
        if (res.success) {
          this.legendItems = res.data.map((item: ChartCountV2) => ({
            label: item.label,
            value: item.count,
            active: true,
          }));
          this.reportCount = this.legendItems.reduce((sum, i) => sum + i.value, 0);
          this.data.labels = this.legendItems.map((x) => x.label);
          this.labels = this.legendItems.map((x) => x.label);
          this.data.datasets[0].data = this.legendItems.map((x) => x.value);

          this.createChart();
        }
      },
    });
  }

  toggleLegend(index: number) {
    const item = this.legendItems[index];
    if (item.active) {
      this.reportCount -= item.value;
    } else {
      this.reportCount += item.value;
    }
    item.active = !item.active;
    this.updateCenterValue(this.reportCount);
  }

  updateCenterValue(total: number) {
    if (!this.chart) return;
    this.chart.$totalValue = total;
    this.chart.update();
  }
  createChart() {
    if (!this.reportChart?.nativeElement) return;
    const canvas = this.reportChart.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    this.chart = new Chart(canvas, this.config);
  }
}

export interface LegendItem {
  label: string;
  value: number;
  active: boolean;
}
